import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import {
  GetUserRelatedTasks_task_booking_Inquiry as GetUserRelatedBookingInquiryGenerated,
  GetUserRelatedTasks_task_booking_Inquiry_tomTasks as GetUserRelatedBookingInquiryTasksDataGenerated,
  GetUserRelatedTasks_task_booking_LeaseInquiry as GetUserRelatedBookingLeaseInquiryGenerated,
  GetUserRelatedTasks_task_booking_LeaseInquiry_tomTasks as GetUserRelatedBookingLeaseInquiryTasksDataGenerated,
  GetUserRelatedTasks_task_booking_TomBooking as GetUserRelatedBookingTomBookingGenerated,
  GetUserRelatedTasks_task as GetUserRelatedTasksDataGenerated,
  GetUserRelatedTasks as GetUserRelatedTasksResponseGenerated,
  GetUserRelatedTasksVariables as GetUserRelatedTasksVariablesGenerated,
} from "core/queries/__generated__/GetUserRelatedTasks";

export interface GetUserRelatedBookingInquiry
  extends GetUserRelatedBookingInquiryGenerated {}
export interface GetUserRelatedBookingLeaseInquiry
  extends GetUserRelatedBookingLeaseInquiryGenerated {}

export interface GetUserRelatedBookingTomBooking
  extends GetUserRelatedBookingTomBookingGenerated {}
export interface GetUserRelatedBookingInquiryTasksData
  extends GetUserRelatedBookingInquiryTasksDataGenerated {}
export interface GetUserRelatedBookingLeaseInquiryTasksData
  extends GetUserRelatedBookingLeaseInquiryTasksDataGenerated {}
export interface GetUserRelatedTasksData
  extends GetUserRelatedTasksDataGenerated {}
export interface GetUserRelatedTasksVariables
  extends GetUserRelatedTasksVariablesGenerated {}
export interface GetUserRelatedTasksResponse
  extends GetUserRelatedTasksResponseGenerated {}

export const BookingFragment = gql`
  fragment BookingFragment on Booking {
    id
    account {
      id
      firstName
      lastName
      numberOfBookings
    }
    checkInTime
    checkOutTime
  }
`;

export const TasksFragment = gql`
  fragment TaskFragment on Task {
    id
    assignee {
      id
      avatar {
        id
        url
      }
      name
      firstName
      lastName
    }
    assignees {
      id
      avatar {
        id
        url
      }
      name
      firstName
      lastName
    }
    booking {
      id
      ...BookingFragment
    }
    city {
      id
    }
    dueAt
    name
    subjectType
    tomTaskRecurrenceSetting {
      id
      daysOfWeek
      intervalAmt
      intervalUnit
      threadId
    }
    type
    unit {
      id
      shortInternalRef
      bookingInfo {
        upcomingBooking {
          id
          checkInTime
        }
      }
      checkInCheckOutInfo {
        isVacantAllDayToday
        checkInToday {
          id
          checkInTime
          checkOutTime
          account {
            id
          }
        }
        checkOutToday {
          id
          checkOutTime
          account {
            id
          }
        }
        occupiedByToday {
          id
          checkOutTime
          account {
            id
          }
        }
      }
    }
  }
  ${BookingFragment}
`;

export const getUserRelatedTasks = gql`
  query GetUserRelatedTasks($taskId: ID!, $pagination: Pagination!) {
    task(id: $taskId) {
      id
      booking {
        id
        ...BookingFragment
        account {
          id
          tasksForPastBookings(pagination: $pagination) {
            cursor
            hasMore
            tasks {
              id
              ...TaskFragment
            }
          }
        }
        tomTasks {
          id
          ...TaskFragment
        }
        ... on Inquiry {
          inquiryId: id
        }
        ... on LeaseInquiry {
          leaseInquiryId: id
          name
        }
      }
    }
  }
  ${BookingFragment}
  ${TasksFragment}
`;

export interface UseGetUserRelatedTasksQueryOptions {
  pollInterval?: number;
  skip?: boolean;
  variables: GetUserRelatedTasksVariables;
}
export const useGetUserRelatedTasks = ({
  pollInterval,
  skip = false,
  variables,
}: UseGetUserRelatedTasksQueryOptions) =>
  useQuery<GetUserRelatedTasksResponse, GetUserRelatedTasksVariables>(
    getUserRelatedTasks,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      pollInterval,
      skip,
      variables,
    },
  );

export const isBookingAnInquiry = (
  booking:
    | GetUserRelatedBookingInquiry
    | GetUserRelatedBookingLeaseInquiry
    | GetUserRelatedBookingTomBooking,
): booking is GetUserRelatedBookingInquiry => "inquiryId" in booking;

export const isBookingALeaseInquiry = (
  booking:
    | GetUserRelatedBookingInquiry
    | GetUserRelatedBookingLeaseInquiry
    | GetUserRelatedBookingTomBooking,
): booking is GetUserRelatedBookingLeaseInquiry => "leaseInquiryId" in booking;

export const isBookingATomBooking = (
  booking:
    | GetUserRelatedBookingInquiry
    | GetUserRelatedBookingLeaseInquiry
    | GetUserRelatedBookingTomBooking,
): booking is GetUserRelatedBookingTomBooking =>
  !("leaseInquiryId" in booking) && !("inquiryId" in booking);

export const getInquiryId = (
  booking:
    | GetUserRelatedBookingInquiry
    | GetUserRelatedBookingLeaseInquiry
    | GetUserRelatedBookingTomBooking
    | null,
) => {
  if (!booking) {
    return null;
  }
  if (isBookingALeaseInquiry(booking)) {
    return booking.leaseInquiryId;
  }
  if (isBookingAnInquiry(booking)) {
    return booking.inquiryId;
  }
  if (isBookingATomBooking(booking)) {
    return booking.id;
  }
  return null;
};
